import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../styles/theme';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../hooks/useScrollEdgeFade';
import {
  deleteCadCanvasImage,
  deleteCadParcel,
  getCadProject,
  saveCadCanvasImage,
  updateCadParcel,
  updateCadProject,
} from '../../services/cadService';
import {
  applyCadParcelPatch,
  isCadMapDisplay,
  isCadPlanDisplay,
  isLngLatBboxMoreComplete,
  readCadPlanBounds,
  removeCadParcelFromProject,
  shouldPaintCadOnMap,
  unprojectCadPlanLngLat,
} from '../utils/cadGeometry';
import {
  CAD_CANVAS_IMAGE_TRANSFORM_RESET,
  clampCadCanvasImageTransform,
  clearCadCanvasImageSizeHint,
  normalizeCadCanvasImageTransform,
  readCadCanvasImage,
  readCadCanvasImageTransform,
  readCadShowPlanImage,
  writeCadCanvasImageSizeHint,
  writeCadCanvasImageTransform,
} from '../utils/cadCanvasImage';
import {
  buildCadProjectCommitPatch,
  hasCadGeorefAnchor,
  isCadRefLotId,
  planCadGeorefCommit,
  readCadRefLotIdForForm,
  stripCadGeorefAnchor,
} from '../utils/cadGeoref';
import { useCadCanvasImageDraft } from '../hooks/useCadCanvasImageDraft';
import {
  previewCadGeorefSticker,
  readCadStickerSrcBounds,
  readCadGeorefSrcCorners,
  rotateCadGeorefSticker,
  scaleCadGeorefSticker,
  stickerFromLotBbox,
  stickerFromWgs84Collection,
  stickerFromGeorefPoints,
  translateCadGeorefSticker,
  cadGeorefStickerMatchesLotSnap,
  canSnapCadGeorefStickerToLot,
} from '../utils/cadGeorefSticker';
import CadGeorefPanel from './CadGeorefPanel';
import CadProjectPinsPanel from './CadProjectPinsPanel';
import { filterCadParcelsByFilters } from '../utils/cadFilterMatch';
import { getCadAreaExtent } from '../data/cadFilterContract';
import { filterCadSellableLots, isCadSellableLot } from '../utils/cadMasterplan';
import {
  mergeCadProjectPinsWithDraft,
  removeCadProjectPinFromProject,
  upsertCadProjectPinInProject,
} from '../utils/cadProjectPins';
import {
  findCadFeature,
  filterCadParcelsForOverlay,
  markCadParcelsForOverlay,
  getCadParcelId,
  getCadParcelInquirySubject,
  getCadParcelLabel,
  getCadParcelTooltipLabel,
  normalizeCadStatusFilter,
  sortCadParcels,
  withCadLotParcelCount,
} from '../utils/cadParcelDisplay';
import CadParcelListMeta from './CadParcelListMeta';
import CadParcelListHeading from './CadParcelListHeading';
import {
  CAD_NOT_FOUND_MESSAGE,
  canEditCadCollection,
  canManageCadProject,
  canPublishCadProject,
  cadProjectQuery,
  getCadProjectId,
  isCadForbidden,
  isCadNotFound,
  isCadUnauthorized,
  mergeCadProjectSummary,
  hydrateCadProjectFromListCard,
} from '../utils/cadAccess';
import {
  getOwnerBranding,
  resolveShareBrandPalette,
  shouldUseBrandedShare,
} from '../../Map/utils/sharedBranding';
import ShowroomManagePanel from './ShowroomManagePanel';
import RecordLeadsSection from '../../Leads/components/RecordLeadsSection';
import RecordCampaignsSection from '../../MetaAds/components/RecordCampaignsSection';
import MarketComparisonCard from '../../MarketComparison/components/MarketComparisonCard';
import { geometryCenter } from '../../MarketComparison/marketComparablesMap';
import ShowroomManageFilters from './ShowroomManageFilters';
import ShowroomParcelListEmpty from './ShowroomParcelListEmpty';
import ShowroomParcelListSkeleton, {
  ShowroomParcelFormSkeleton,
} from './ShowroomParcelListSkeleton';
import ShowroomParcelReadout from './ShowroomParcelReadout';
import BrandedCadParcelDetail from './BrandedCadParcelDetail';
import { showroomCardSx, showroomScrollSx, showroomToolbarSx } from '../Showroom.styles';
import { indexCadProjectCommercial, onCommercialChanged } from '../../Leads/commercialSummary';

function CadVisitorParcelList({
  features,
  selectedId,
  onSelect,
  parcelSearch,
  onParcelSearchChange,
  statusFilter,
  onStatusFilterChange,
  branding = null,
}) {
  const [sortBy, setSortBy] = useState('name');
  const detailScrollRef = useRef(null);
  const { scrollRef: detailFadeRef, fadeTop: detailFadeTop, fadeBottom: detailFadeBottom } = useScrollEdgeFade();
  const { scrollRef: listFadeRef, fadeTop: listFadeTop, fadeBottom: listFadeBottom } = useScrollEdgeFade();
  const setDetailScrollNode = useCallback((node) => {
    detailScrollRef.current = node;
    detailFadeRef(node);
  }, [detailFadeRef]);
  const counts = useMemo(() => {
    const next = {};
    features.forEach((feature) => {
      const status = feature?.properties?.stock_status || 'available';
      next[status] = (next[status] || 0) + 1;
    });
    return next;
  }, [features]);
  const visible = useMemo(
    () => sortCadParcels(
      filterCadParcelsForOverlay(features, { query: parcelSearch, statusFilter }),
      sortBy,
    ),
    [features, parcelSearch, statusFilter, sortBy],
  );
  const selected = findCadFeature({ features }, selectedId)
    || features.find((feature) => getCadParcelId(feature) === selectedId);
  const brandPalette = branding
    ? resolveShareBrandPalette(branding)
    : null;

  useEffect(() => {
    if (!selectedId) return;
    const el = detailScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [selectedId]);

  if (selected) {
    return (
      <Box
        ref={setDetailScrollNode}
        sx={{
          ...showroomScrollSx,
          py: 1.5,
          ...getScrollEdgeFadeSx(detailFadeTop, detailFadeBottom),
        }}
      >
        {brandPalette ? (
          <BrandedCadParcelDetail
            feature={selected}
            accentColor={brandPalette.primary}
          />
        ) : (
          <ShowroomParcelReadout feature={selected} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box sx={showroomToolbarSx}>
          <ShowroomManageFilters
            searchQuery={parcelSearch}
            onSearchChange={onParcelSearchChange}
            statusFilter={statusFilter}
            onStatusChange={onStatusFilterChange}
            featuresCount={features.length}
            counts={counts}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
      </Box>
      <Box
        ref={listFadeRef}
        sx={{
          ...showroomScrollSx,
          gap: 1,
          py: 1.5,
          ...getScrollEdgeFadeSx(listFadeTop, listFadeBottom),
        }}
      >
        {visible.length === 0 ? (
          <ShowroomParcelListEmpty
            hasActiveFilter={Boolean(
              String(parcelSearch || '').trim() || normalizeCadStatusFilter(statusFilter),
            )}
          />
        ) : visible.map((feature) => {
          const id = getCadParcelId(feature);
          const props = feature.properties || {};
          return (
            <Box key={id} sx={showroomCardSx} onClick={() => onSelect(id)}>
              <CadParcelListHeading feature={feature} />
              <CadParcelListMeta props={props} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Un solo aviso al confirmar: cómo queda la lista después de Guardar. */
function cadGeorefCommitMessage(plan) {
  if (plan.onMapAfter) return 'Lista referenciada en el mapa.';
  if (plan.hasImageAfter) return 'Lista sobre la imagen base.';
  return 'Lista sobre el lienzo.';
}

/**
 * Detalle de un showroom CAD dentro del panel Listas. No cambia de ruta.
 * El overlay MapLibre (modo map) lo pinta MapPage vía `onOverlayChange`.
 * Canvas/image abren un MapLibre propio (sin teselas) en el viewport del mapa.
 * Owner: ⋮ Referenciar ubica el plano (mapa y/o ilustración).
 * Con `spaceMirror` (espacio ajeno) no se ofrece eliminar lotes ni pines.
 * No mandar metros al mapa país.
 */
export default function CadInListsDetail({
  listProject,
  initialParcelId = null,
  onBack,
  onOverlayChange,
  sharePatch = null,
  onParcelCountsChange,
  onParcelOpenChange,
  deselectParcelRef,
  startGeorefRef,
  autoStartGeoref = false,
  /** Filtros `cad-list-v1` del header. Sin esto, el detalle filtra solo local. */
  listFilters = null,
  onFilterValueChange,
  onAreaExtentChange,
  georefPickRequest = null,
  pinPlaceRequest = null,
  onProjectLoaded,
  onProjectReloaded,
  onLocateLot,
  onQueryCadRefLotBbox,
  onRefLotHighlightChange,
  onGeorefOpenChange,
  startPinsRef,
  inquiryByItem = null,
  /** Espacio ajeno (invitado): eliminar lotes y pines de la lista es solo del dueño. */
  spaceMirror = false,
  /** Owner de las escrituras en espacio ajeno (`owner_user_id`); propio → null. Leads. */
  spaceOwnerUserId = null,
  /** Abre un lead desde "Consultas y leads" de la parcela: `(leadId, returnCtx)`. */
  onOpenLead = undefined,
  /** «Ver en el mapa» de la comparación de mercado → modo Comparables del mapa principal. */
  onShowMarketComparables = undefined,
}) {
  const slug = listProject?.slug || '';
  const projectId = getCadProjectId(listProject);
  const projectLookup = useMemo(
    () => cadProjectQuery({ id: projectId, slug }),
    [projectId, slug],
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingParcel, setDeletingParcel] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState(listProject);
  // Resumen comercial por parcela (paso 6). Aparte de `project`: las recargas
  // sin include (guardar, georef) no lo borran.
  const [commercialById, setCommercialById] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    const id = initialParcelId == null ? '' : String(initialParcelId).trim();
    return id || null;
  });
  const [localStatusFilter, setLocalStatusFilter] = useState(null);
  const [parcelSearch, setParcelSearch] = useState('');
  const [georefOpen, setGeorefOpen] = useState(false);
  const [pinsOpen, setPinsOpen] = useState(false);
  const [pinPlacing, setPinPlacing] = useState(false);
  const [pinPlaceCoords, setPinPlaceCoords] = useState(null);
  const [pinDraft, setPinDraft] = useState(null);
  const [pinHoverId, setPinHoverId] = useState(null);
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [georefLotId, setGeorefLotId] = useState('');
  const [georefSticker, setGeorefSticker] = useState(null);
  const [georefLotBbox, setGeorefLotBbox] = useState(null);
  const [georefEditing, setGeorefEditing] = useState(false);
  const [georefFitNonce, setGeorefFitNonce] = useState(0);
  const [georefSaving, setGeorefSaving] = useState(false);
  const [clearFromMapPending, setClearFromMapPending] = useState(false);
  const [adjustingImage, setAdjustingImage] = useState(false);
  const [showPlanImageDraft, setShowPlanImageDraft] = useState(null);
  const [pickingLot, setPickingLot] = useState(false);
  const [imageTransform, setImageTransform] = useState(
    () => readCadCanvasImageTransform(listProject),
  );
  const autoGeorefDoneRef = useRef(false);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const onOverlayChangeRef = useRef(onOverlayChange);
  onOverlayChangeRef.current = onOverlayChange;
  const sharePatchRef = useRef(sharePatch);
  sharePatchRef.current = sharePatch;
  const onParcelCountsChangeRef = useRef(onParcelCountsChange);
  onParcelCountsChangeRef.current = onParcelCountsChange;
  const onParcelOpenChangeRef = useRef(onParcelOpenChange);
  onParcelOpenChangeRef.current = onParcelOpenChange;
  const onProjectLoadedRef = useRef(onProjectLoaded);
  onProjectLoadedRef.current = onProjectLoaded;
  const onProjectReloadedRef = useRef(onProjectReloaded);
  onProjectReloadedRef.current = onProjectReloaded;
  const onRefLotHighlightChangeRef = useRef(onRefLotHighlightChange);
  onRefLotHighlightChangeRef.current = onRefLotHighlightChange;
  const onGeorefOpenChangeRef = useRef(onGeorefOpenChange);
  onGeorefOpenChangeRef.current = onGeorefOpenChange;
  const onAreaExtentChangeRef = useRef(onAreaExtentChange);
  onAreaExtentChangeRef.current = onAreaExtentChange;
  const onQueryCadRefLotBboxRef = useRef(onQueryCadRefLotBbox);
  onQueryCadRefLotBboxRef.current = onQueryCadRefLotBbox;
  const listProjectRef = useRef(listProject);
  listProjectRef.current = listProject;
  const georefPickRequestRef = useRef(georefPickRequest);
  georefPickRequestRef.current = georefPickRequest;
  const georefPickConsumedTsRef = useRef(0);
  const appliedInitialParcelRef = useRef('');
  const initialParcelIdRef = useRef(initialParcelId);
  initialParcelIdRef.current = initialParcelId;
  const georefPendingLotFitRef = useRef(false);
  const georefStickerRef = useRef(null);
  const georefLotBboxRef = useRef(null);
  const projectRef = useRef(project);
  projectRef.current = project;
  georefStickerRef.current = georefSticker;
  georefLotBboxRef.current = georefLotBbox;

  const remoteImage = readCadCanvasImage(project);
  const imageDraft = useCadCanvasImageDraft({
    projectId,
    hasRemoteImage: Boolean(remoteImage),
  });
  const imageDraftRef = useRef(imageDraft);
  imageDraftRef.current = imageDraft;

  useEffect(() => {
    if (!projectLookup.id) {
      setError('Falta el id del proyecto.');
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const pendingId = String(initialParcelIdRef.current || '').trim();
    setSelectedId(pendingId || null);
    setParcelSearch('');
    setGeorefOpen(false);
    setGeorefLotId('');
    setGeorefSticker(null);
    setGeorefLotBbox(null);
    setGeorefEditing(false);
    setGeorefFitNonce(0);
    setClearFromMapPending(false);
    setAdjustingImage(false);
    setShowPlanImageDraft(null);
    setPickingLot(false);
    (async () => {
      try {
        // Resumen comercial en la primera carga (detalle autenticado; nunca share ni
        // mapa público): la API lo ignora si `viewer_can_manage` es false.
        const result = await getCadProject(projectLookup, {
          signal: controller.signal,
          includeCommercialSummary: true,
        });
        if (controller.signal.aborted) return;
        if (isCadUnauthorized(result._httpStatus)) {
          toast.error('Iniciá sesión de nuevo para ver esta lista.');
          onBackRef.current?.();
          return;
        }
        if (isCadForbidden(result._httpStatus) || isCadNotFound(result._httpStatus)) {
          toast.error(CAD_NOT_FOUND_MESSAGE);
          onBackRef.current?.();
          return;
        }
        if (!result.ok) {
          setError(result.message || 'No se pudo cargar la lista.');
          setLoading(false);
          return;
        }
        const patch = sharePatchRef.current;
        const sameSlug = !patch?.slug || patch.slug === slug;
        const loaded = patch && sameSlug
          ? mergeCadProjectSummary(result.project, patch)
          : result.project;
        const nextProject = hydrateCadProjectFromListCard(loaded, listProjectRef.current);
        setCommercialById(indexCadProjectCommercial(result.project, getCadParcelId));
        setProject(nextProject);
        setImageTransform(readCadCanvasImageTransform(nextProject));
        onProjectLoadedRef.current?.(nextProject);
        setLoading(false);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('CAD en listas: fallo al cargar el proyecto', err);
        setError('No se pudo cargar la lista.');
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [projectLookup, slug]);

  // Refetch del resumen tras `terrio:commercial-changed` (debounce 400 ms), con
  // los mismos parámetros que la primera carga; sin recalcular en el cliente.
  const reloadCommercial = useCallback(async (signal) => {
    try {
      const res = await getCadProject(projectLookup, { signal, includeCommercialSummary: true });
      if (signal?.aborted || !res.ok) return;
      setCommercialById(indexCadProjectCommercial(res.project, getCadParcelId));
    } catch (_) { /* se mantiene el último */ }
  }, [projectLookup]);

  useEffect(() => {
    setCommercialById(null);
  }, [projectLookup]);

  useEffect(() => {
    let timer = null;
    const off = onCommercialChanged(() => {
      clearTimeout(timer);
      timer = setTimeout(() => { reloadCommercial(); }, 400);
    });
    return () => {
      clearTimeout(timer);
      off();
    };
  }, [reloadCommercial]);

  useEffect(() => {
    appliedInitialParcelRef.current = '';
  }, [slug, initialParcelId]);

  useEffect(() => {
    const id = initialParcelId == null ? '' : String(initialParcelId).trim();
    if (loading || !project || !id) return;
    const key = `${slug}:${id}`;
    if (appliedInitialParcelRef.current === key) return;
    appliedInitialParcelRef.current = key;
    setSelectedId(id);
  }, [loading, project, initialParcelId, slug]);

  useEffect(() => () => {
    onOverlayChangeRef.current?.({ enabled: false, mode: 'off' });
  }, []);

  const canManage = canManageCadProject(project);
  const canPublish = canPublishCadProject(project);
  const canEditCollection = canEditCadCollection(project) && !canManage;
  const visitorBranding = !canManage && !canEditCollection && shouldUseBrandedShare(project)
    ? getOwnerBranding(project)
    : null;
  const canPaintOnMap = shouldPaintCadOnMap(project);
  const planDisplay = isCadPlanDisplay(project);
  // Lo que el dueño ve mientras edita: el borrador manda sobre lo guardado.
  const canvasImage = useMemo(() => {
    if (imageDraft.draft) return { url: imageDraft.draft.objectUrl, key: null };
    return imageDraft.removed ? null : remoteImage;
  }, [imageDraft.draft, imageDraft.removed, remoteImage]);
  const imagePaintUrl = imageDraft.draft?.objectUrl
    || (!imageDraft.removed ? (canvasImage?.url || null) : null);
  const hasCanvasImage = Boolean(canvasImage);
  const adjustImage = Boolean(canPublish && georefOpen && adjustingImage && hasCanvasImage);
  const showPlanImage = showPlanImageDraft ?? readCadShowPlanImage({
    ...project,
    canvas_image: hasCanvasImage ? (canvasImage || project?.canvas_image) : null,
  });
  const georefDirty = Boolean(
    imageDraft.dirty
    || clearFromMapPending
    || georefSticker
    || (georefOpen && showPlanImage !== readCadShowPlanImage(project)),
  );

  /**
   * El pipeline del mapa lee el proyecto, no el borrador: se le pasa el estado
   * al que va a quedar si el dueño guarda. `georef` queda intacto porque de ahí
   * salen los `srcBounds` que ubican la imagen.
   */
  const previewProject = useMemo(() => {
    if (!project) return project;
    let next = project;
    if (georefOpen) {
      let nextImage = project.canvas_image;
      if (canvasImage !== remoteImage) {
        // `bounds` es del DXF, no del archivo: sobrevive al reemplazo.
        nextImage = canvasImage ? { ...(project.canvas_image || {}), ...canvasImage } : null;
      }
      const nextMode = clearFromMapPending
        ? (hasCanvasImage ? 'image' : 'canvas')
        : project.display_mode;
      if (nextImage !== project.canvas_image || nextMode !== project.display_mode) {
        next = { ...project, canvas_image: nextImage, display_mode: nextMode };
      }
    }
    if (readCadShowPlanImage(next) === showPlanImage) return next;
    return { ...next, show_plan_image: showPlanImage };
  }, [
    canvasImage,
    clearFromMapPending,
    georefOpen,
    hasCanvasImage,
    project,
    remoteImage,
    showPlanImage,
  ]);

  const pinsPaintProject = useMemo(
    () => mergeCadProjectPinsWithDraft(previewProject, pinDraft),
    [pinDraft, previewProject],
  );

  const imageSrcBounds = useMemo(() => {
    if (georefSticker?.srcBounds) return georefSticker.srcBounds;
    const fromCorners = readCadStickerSrcBounds(project);
    if (fromCorners) return fromCorners;
    if (isCadMapDisplay(project)) return null;
    return readCadPlanBounds(project);
  }, [georefSticker, project]);

  // El encaje tampoco impacta hasta Guardar: acá solo se mueve la vista.
  const commitImageTransform = useCallback((next) => {
    setImageTransform(clampCadCanvasImageTransform(
      normalizeCadCanvasImageTransform(next),
      imageSrcBounds,
    ));
  }, [imageSrcBounds]);

  useEffect(() => {
    if (!imageSrcBounds) return;
    const clamped = clampCadCanvasImageTransform(imageTransform, imageSrcBounds);
    if (
      clamped.dx === imageTransform.dx
      && clamped.dy === imageTransform.dy
      && clamped.scale === imageTransform.scale
      && clamped.scaleX === imageTransform.scaleX
      && clamped.scaleY === imageTransform.scaleY
    ) {
      return;
    }
    commitImageTransform(clamped);
  }, [commitImageTransform, imageSrcBounds, imageTransform]);

  const features = useMemo(
    () => (Array.isArray(project?.parcels?.features) ? project.parcels.features : []),
    [project],
  );
  const parcelCounts = useMemo(() => {
    const lots = filterCadSellableLots(features);
    let available = 0;
    lots.forEach((feature) => {
      if ((feature?.properties?.stock_status || 'available') === 'available') {
        available += 1;
      }
    });
    return { total: lots.length, available };
  }, [features]);

  useEffect(() => {
    onAreaExtentChangeRef.current?.(getCadAreaExtent(filterCadSellableLots(features)));
  }, [features]);

  useEffect(() => () => {
    onAreaExtentChangeRef.current?.(null);
  }, []);

  /**
   * El estado es una key más de los filtros del header (`stock_status`), para
   * que “disponibles o en oferta” del buscador caiga en el mismo lugar que el
   * dropdown. Sin header (o sin sesión), estado local.
   */
  const controlled = Boolean(listFilters && onFilterValueChange);
  const headerFiltersWithoutStock = useMemo(() => {
    if (!listFilters) return null;
    const { stock_status: omitted, ...rest } = listFilters;
    return rest;
  }, [listFilters]);
  const statusFilter = controlled ? listFilters.stock_status : localStatusFilter;
  const setStatusFilter = useCallback((next) => {
    if (controlled) onFilterValueChange('stock_status', next);
    else setLocalStatusFilter(next);
  }, [controlled, onFilterValueChange]);

  // Los sub-paneles reciben lo que ya cumple superficie / precio / uso; el
  // estado y el texto los siguen resolviendo ellos (conteos del dropdown).
  const scopedFeatures = useMemo(
    () => (controlled ? filterCadParcelsByFilters(features, headerFiltersWithoutStock) : features),
    [controlled, features, headerFiltersWithoutStock],
  );

  // Owner: el listado filtra a lotes; calle/verde/predio van en `features`
  // para que el click en el mapa encuentre la ficha.
  const manageListFeatures = useMemo(() => {
    if (canEditCollection) return scopedFeatures;
    const seen = new Set(scopedFeatures.map((feature) => getCadParcelId(feature)));
    const context = features.filter((feature) => {
      if (isCadSellableLot(feature)) return false;
      const id = getCadParcelId(feature);
      return Boolean(id) && !seen.has(id);
    });
    return context.length ? [...scopedFeatures, ...context] : scopedFeatures;
  }, [canEditCollection, features, scopedFeatures]);

  const markForOverlay = useCallback((list) => {
    const scoped = controlled
      ? filterCadParcelsByFilters(list, headerFiltersWithoutStock)
      : list;
    const marked = markCadParcelsForOverlay(scoped, { query: parcelSearch, statusFilter });
    if (scoped.length === list.length) return marked;
    // Las que no cumplen los filtros del header siguen dibujadas, muteadas.
    const matched = new Set(
      marked
        .filter((feature) => feature?.properties?.filter_match !== false)
        .map((feature) => getCadParcelId(feature)),
    );
    return list.map((feature) => ({
      ...feature,
      properties: {
        ...(feature?.properties || {}),
        filter_match: isCadSellableLot(feature)
          ? matched.has(getCadParcelId(feature))
          : true,
      },
    }));
  }, [controlled, headerFiltersWithoutStock, parcelSearch, statusFilter]);

  const mapFeatureCollection = useMemo(() => {
    if (!project?.parcels) return null;
    return { ...project.parcels, features: markForOverlay(features) };
  }, [features, markForOverlay, project]);
  const georefPreviewCollection = useMemo(() => {
    if (!georefOpen || !georefSticker) return null;
    const preview = previewCadGeorefSticker(project, georefSticker);
    if (!preview) return null;
    return { ...preview, features: markForOverlay(preview.features) };
  }, [georefOpen, georefSticker, markForOverlay, project]);

  const closeGeoref = useCallback(() => {
    georefPendingLotFitRef.current = false;
    setGeorefOpen(false);
    setGeorefSticker(null);
    setGeorefLotId('');
    setGeorefLotBbox(null);
    setGeorefEditing(false);
    setGeorefFitNonce(0);
    setGeorefSaving(false);
    setClearFromMapPending(false);
    setAdjustingImage(false);
    setShowPlanImageDraft(null);
    setPickingLot(false);
    imageDraftRef.current.reset();
    setImageTransform(readCadCanvasImageTransform(projectRef.current));
  }, []);

  const closePins = useCallback(() => {
    setPinsOpen(false);
    setPinPlacing(false);
    setPinPlaceCoords(null);
    setPinDraft(null);
    setPinHoverId(null);
    setSelectedPinId(null);
  }, []);

  const startPins = useCallback(() => {
    closeGeoref();
    setSelectedId(null);
    setPinsOpen(true);
    setPinPlacing(false);
    setPinPlaceCoords(null);
    setPinDraft(null);
    setPinHoverId(null);
    setSelectedPinId(null);
  }, [closeGeoref]);

  const startGeoref = useCallback(() => {
    closePins();
    const pendingTs = Number(georefPickRequestRef.current?.ts) || 0;
    if (pendingTs) georefPickConsumedTsRef.current = pendingTs;
    setClearFromMapPending(false);
    setAdjustingImage(false);
    setSelectedId(null);
    setGeorefOpen(true);
    const current = projectRef.current;
    setGeorefLotId(
      readCadRefLotIdForForm(current) || readCadRefLotIdForForm(listProjectRef.current),
    );
    setGeorefLotBbox(null);
    georefPendingLotFitRef.current = false;
    const editing = hasCadGeorefAnchor(current);
    setGeorefEditing(editing);
    const srcCorners = readCadGeorefSrcCorners(current);
    const srcBounds = readCadStickerSrcBounds(current);
    const fromPoints = editing
      ? stickerFromGeorefPoints(
        srcBounds,
        current?.georef?.points,
        srcCorners,
      )
      : null;
    const existing = fromPoints
      || (editing && shouldPaintCadOnMap(current) && srcBounds
        ? stickerFromWgs84Collection(srcBounds, current?.parcels, srcCorners)
        : null);
    setGeorefSticker(existing);
    if (existing) setGeorefFitNonce((n) => n + 1);
    // Con ilustración y sin mapa, el lienzo. Sin ilustración, el mapa para el lote.
    setPickingLot(!existing && !readCadCanvasImage(current));
  }, [closePins]);

  // Recién creada desde un plano: se abre Referenciar sin que lo busque en el ⋮.
  // Cancelar deja la lista en canvas; por eso no se reintenta.
  useEffect(() => {
    if (!autoStartGeoref || autoGeorefDoneRef.current) return;
    if (loading || error || !project) return;
    if (!canPublishCadProject(project) || hasCadGeorefAnchor(project)) return;
    autoGeorefDoneRef.current = true;
    startGeoref();
  }, [autoStartGeoref, error, loading, project, startGeoref]);

  useEffect(() => {
    onGeorefOpenChangeRef.current?.(georefOpen);
  }, [georefOpen]);

  useEffect(() => () => {
    onGeorefOpenChangeRef.current?.(false);
  }, []);

  useEffect(() => {
    if (!startGeorefRef) return undefined;
    startGeorefRef.current = startGeoref;
    return () => {
      startGeorefRef.current = null;
    };
  }, [startGeoref, startGeorefRef]);

  useEffect(() => {
    if (!startPinsRef) return undefined;
    startPinsRef.current = startPins;
    return () => {
      startPinsRef.current = null;
    };
  }, [startPins, startPinsRef]);

  const applyReloadedProject = useCallback((loaded) => {
    const next = hydrateCadProjectFromListCard(loaded, listProjectRef.current);
    projectRef.current = next;
    setProject(next);
    onProjectReloadedRef.current?.(next);
    return next;
  }, []);

  /** Elegir imagen no vincula nada: sube a S3 y espera el Guardar. */
  const handleImagePick = useCallback((file) => {
    imageDraftRef.current.pickImage(file);
    setImageTransform(CAD_CANVAS_IMAGE_TRANSFORM_RESET);
    setPickingLot(false);
    setAdjustingImage(true);
  }, []);

  const handleImageRemove = useCallback(() => {
    imageDraftRef.current.removeImage();
    setImageTransform(CAD_CANVAS_IMAGE_TRANSFORM_RESET);
    setAdjustingImage(false);
  }, []);

  const handleImageUndoRemove = useCallback(() => {
    imageDraftRef.current.undoRemove();
    setImageTransform(readCadCanvasImageTransform(projectRef.current));
  }, []);

  /** Sacar el loteo del mapa también espera: el plano base no se toca. */
  const handleClearFromMap = useCallback(() => {
    georefPendingLotFitRef.current = false;
    setClearFromMapPending(true);
    setGeorefSticker(null);
    setGeorefLotId('');
    setGeorefLotBbox(null);
    setPickingLot(false);
  }, []);

  const handleUndoClearFromMap = useCallback(() => {
    setClearFromMapPending(false);
    startGeoref();
  }, [startGeoref]);

  const placeStickerOnLotBbox = useCallback((bbox, { fit = true } = {}) => {
    const project = projectRef.current;
    const next = stickerFromLotBbox(
      readCadStickerSrcBounds(project),
      bbox,
      readCadGeorefSrcCorners(project),
    );
    if (!next) return;
    setGeorefLotBbox(bbox);
    setGeorefSticker(next);
    setPickingLot(false);
    if (fit) setGeorefFitNonce((n) => n + 1);
  }, []);

  const snapStickerToMotherLot = useCallback(() => {
    if (!Array.isArray(georefLotBbox) || georefLotBbox.length < 4) return;
    placeStickerOnLotBbox(georefLotBbox, { fit: false });
  }, [georefLotBbox, placeStickerOnLotBbox]);

  const clearMotherLot = useCallback(() => {
    georefPendingLotFitRef.current = true;
    setGeorefLotId('');
    setGeorefLotBbox(null);
    setGeorefSticker(null);
    setAdjustingImage(false);
    setPickingLot(true);
  }, []);

  const pickLotOnMap = useCallback(() => {
    setAdjustingImage(false);
    setPickingLot(true);
  }, []);

  useEffect(() => {
    const req = georefPickRequest;
    if (!req?.ts || !georefOpen) return;
    if (req.ts <= georefPickConsumedTsRef.current) return;
    georefPickConsumedTsRef.current = req.ts;
    if (req.kind === 'lot' && req.lotId) {
      georefPendingLotFitRef.current = true;
      setGeorefLotId(req.lotId);
      if (Array.isArray(req.bbox) && req.bbox.length >= 4) {
        placeStickerOnLotBbox(req.bbox);
      }
    }
  }, [georefOpen, georefPickRequest, placeStickerOnLotBbox]);

  useEffect(() => {
    if (!georefOpen || !isCadRefLotId(georefLotId)) return undefined;
    const resolve = () => {
      const bbox = onQueryCadRefLotBboxRef.current?.(georefLotId);
      if (!Array.isArray(bbox) || bbox.length < 4) return false;
      const prev = georefLotBboxRef.current;
      const sticker = georefStickerRef.current;
      const srcBounds = readCadStickerSrcBounds(projectRef.current);
      const pending = georefPendingLotFitRef.current;
      const stillSnapped = !sticker
        || cadGeorefStickerMatchesLotSnap(sticker, srcBounds, prev);
      if (pending) {
        if (!prev || isLngLatBboxMoreComplete(bbox, prev)) {
          placeStickerOnLotBbox(bbox);
        }
        georefPendingLotFitRef.current = false;
      } else if (prev && isLngLatBboxMoreComplete(bbox, prev) && stillSnapped) {
        placeStickerOnLotBbox(bbox);
      } else if (!prev) {
        setGeorefLotBbox(bbox);
      }
      return true;
    };
    resolve();
    const soon = window.setTimeout(resolve, 250);
    const later = window.setTimeout(resolve, 800);
    const last = window.setTimeout(resolve, 1800);
    return () => {
      window.clearTimeout(soon);
      window.clearTimeout(later);
      window.clearTimeout(last);
    };
  }, [georefLotId, georefOpen, placeStickerOnLotBbox]);

  useEffect(() => {
    if (!georefOpen || !isCadRefLotId(georefLotId)) {
      onRefLotHighlightChangeRef.current?.(null);
      return;
    }
    onRefLotHighlightChangeRef.current?.({ lot_id: String(georefLotId).trim() });
  }, [georefOpen, georefLotId]);

  useEffect(() => () => {
    onRefLotHighlightChangeRef.current?.(null);
  }, []);

  /**
   * Guardar es lo único que impacta. Primero la imagen (vincular o quitar),
   * después el proyecto (georef, vista y encaje) en un solo PATCH, un reload
   * y un aviso.
   */
  const handleGeorefSubmit = useCallback(async () => {
    if (!projectLookup.id) return;
    const draftState = imageDraftRef.current;
    const current = projectRef.current;
    setGeorefSaving(true);
    try {
      const uploadedImage = draftState.draft ? await draftState.waitForUpload() : null;
      if (draftState.draft && !uploadedImage?.s3Key) {
        toast.error('No se pudo subir la imagen. Probá de nuevo.');
        return;
      }
      const plan = planCadGeorefCommit({
        uploadedImage,
        removed: draftState.removed,
        clearFromMap: clearFromMapPending,
        hasRemoteImage: Boolean(readCadCanvasImage(current)),
        wasOnMap: hasCadGeorefAnchor(current),
        sticker: georefSticker,
      });
      const showPlanImageDirty = showPlanImage !== readCadShowPlanImage(current);
      if (plan.empty && !showPlanImageDirty) {
        closeGeoref();
        return;
      }
      if (plan.saveImage) {
        const saved = await saveCadCanvasImage({
          id: projectLookup.id,
          s3Key: plan.saveImage.s3Key,
          s3Url: plan.saveImage.s3Url,
          imageUrl: plan.saveImage.s3Url,
        });
        if (!saved.ok) {
          toast.error(saved.message || 'No se pudo guardar la imagen base.');
          return;
        }
      } else if (plan.deleteImage) {
        const dropped = await deleteCadCanvasImage({ id: projectLookup.id });
        if (!dropped.ok) {
          toast.error(dropped.message || 'No se pudo quitar la imagen base.');
          return;
        }
      }
      const patch = buildCadProjectCommitPatch({
        id: projectLookup.id,
        plan,
        refLotId: georefLotId,
        sticker: georefSticker,
        transform: imageTransform,
        showPlanImage: plan.hasImageAfter ? showPlanImage : undefined,
      });
      if (patch) {
        const result = await updateCadProject(patch);
        if (!result.ok) {
          toast.error(result.message || 'No se pudo guardar la referencia.');
          return;
        }
      }
      // Espejo de este browser hasta que el GET devuelva `canvas_image.transform`.
      if (plan.hasImageAfter) {
        writeCadCanvasImageTransform(current, imageTransform);
        if (draftState.draft?.size) writeCadCanvasImageSizeHint(current, draftState.draft.size);
      } else {
        writeCadCanvasImageTransform(current, CAD_CANVAS_IMAGE_TRANSFORM_RESET);
        clearCadCanvasImageSizeHint(current);
      }
      const reloaded = await getCadProject(projectLookup);
      if (!reloaded.ok || !reloaded.project) {
        toast.error(reloaded.message || 'Se guardó, pero no se pudo recargar la lista.');
        return;
      }
      applyReloadedProject(plan.georef === 'clear'
        ? stripCadGeorefAnchor(reloaded.project)
        : reloaded.project);
      closeGeoref();
      toast.success(cadGeorefCommitMessage(plan));
    } catch (err) {
      console.error('CAD georef: no se pudo guardar', err);
      toast.error('No se pudo guardar.');
    } finally {
      setGeorefSaving(false);
    }
  }, [
    applyReloadedProject,
    clearFromMapPending,
    closeGeoref,
    georefLotId,
    georefSticker,
    imageTransform,
    projectLookup,
    showPlanImage,
  ]);

  /** Cerrar con cambios sin confirmar los tira: el archivo no queda vinculado. */
  const handleGeorefCancel = useCallback(async () => {
    if (georefDirty) {
      const confirm = await confirmDialog({
        title: '¿Descartar los cambios?',
        description: 'La lista queda como estaba. Si elegiste una imagen, se pierde.',
        confirmLabel: 'Descartar',
        cancelLabel: 'Seguir editando',
        variant: 'danger',
      });
      if (!confirm?.isConfirmed) return;
    }
    closeGeoref();
  }, [closeGeoref, georefDirty]);

  const handleStickerTranslate = useCallback(({ dlng, dlat }) => {
    setGeorefSticker((prev) => translateCadGeorefSticker(prev, dlng, dlat));
  }, []);

  const handlePinSelect = useCallback((pin) => {
    if (!pin?.id) return;
    setSelectedPinId(pin.id);
  }, []);

  const handlePlanPinPlace = useCallback(({ lng, lat }) => {
    if (!pinPlacing) return;
    const bounds = readCadPlanBounds(projectRef.current);
    const local = unprojectCadPlanLngLat(lng, lat, bounds);
    if (!local) return;
    setPinPlaceCoords({ local_x: local[0], local_y: local[1], ts: Date.now() });
  }, [pinPlacing]);

  const handleProjectPinsChange = useCallback((action, pin) => {
    setProject((prev) => {
      if (!prev) return prev;
      if (action === 'delete') return removeCadProjectPinFromProject(prev, pin?.id);
      return upsertCadProjectPinInProject(prev, pin);
    });
  }, []);

  const pinPlaceAcceptAfterRef = useRef(0);

  useEffect(() => {
    if (!pinPlacing) return;
    pinPlaceAcceptAfterRef.current = Date.now();
    setPinPlaceCoords(null);
  }, [pinPlacing]);

  useEffect(() => {
    const req = pinPlaceRequest;
    if (!req?.ts || !pinsOpen || !pinPlacing) return;
    if (req.ts <= pinPlaceAcceptAfterRef.current) return;
    const lng = Number(req.lng);
    const lat = Number(req.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    setPinPlaceCoords({ lng, lat, ts: req.ts });
  }, [pinPlaceRequest, pinsOpen, pinPlacing]);

  useEffect(() => {
    const report = onOverlayChangeRef.current;
    if (!report) return;
    if (loading || error) {
      report({ enabled: false, mode: 'off' });
      return;
    }
    if (georefOpen) {
      if (georefPreviewCollection) {
        report({
          enabled: true,
          mode: 'map',
          project: previewProject,
          featureCollection: georefPreviewCollection,
          selectedId: null,
          onSelect: null,
          georefPick: 'lot',
          georefSticker: true,
          canvasImageSticker: georefSticker,
          fitNonce: georefFitNonce,
          onStickerTranslate: handleStickerTranslate,
          imageTransform,
          imagePaintUrl,
          onImageTransformChange: commitImageTransform,
          adjustImage,
        });
        return;
      }
      // Sin lote madre: ilustración → lienzo. Si pidió el mapa, queda libre
      // para el click. Sin ninguna de las dos, también el mapa (lote madre).
      if (hasCanvasImage && !pickingLot) {
        report({
          enabled: false,
          mode: 'plan',
          project: previewProject,
          featureCollection: mapFeatureCollection,
          selectedId: null,
          onSelect: null,
          imageTransform,
          imagePaintUrl,
          onImageTransformChange: commitImageTransform,
          adjustImage,
        });
        return;
      }
      report({
        enabled: false,
        mode: 'off',
        georefPick: 'lot',
        georefSticker: false,
      });
      return;
    }
    if (pinsOpen) {
      const pinOverlay = {
        project: pinsPaintProject,
        featureCollection: mapFeatureCollection,
        selectedId: null,
        onSelect: null,
        onPinSelect: handlePinSelect,
        highlightedPinId: pinHoverId,
        pinPick: pinPlacing,
        imageTransform,
        imagePaintUrl,
      };
      if (planDisplay) {
        report({
          ...pinOverlay,
          enabled: false,
          mode: 'plan',
          onPlanClick: pinPlacing ? handlePlanPinPlace : null,
        });
        return;
      }
      if (!canPaintOnMap) {
        report({ enabled: false, mode: 'off', pinPick: pinPlacing });
        return;
      }
      report({
        ...pinOverlay,
        enabled: true,
        mode: 'map',
      });
      return;
    }
    if (planDisplay) {
      report({
        enabled: false,
        mode: 'plan',
        project: previewProject,
        featureCollection: mapFeatureCollection,
        selectedId,
        onSelect: setSelectedId,
        onPinSelect: null,
        imageTransform,
        imagePaintUrl,
      });
      return;
    }
    if (!canPaintOnMap) {
      report({ enabled: false, mode: 'off' });
      return;
    }
    report({
      enabled: true,
      mode: 'map',
      project: previewProject,
      featureCollection: mapFeatureCollection,
      selectedId,
      onSelect: setSelectedId,
      onPinSelect: null,
      imageTransform,
      imagePaintUrl,
    });
  }, [
    adjustImage,
    canPaintOnMap,
    canvasImage,
    hasCanvasImage,
    imagePaintUrl,
    commitImageTransform,
    error,
    georefFitNonce,
    georefOpen,
    georefPreviewCollection,
    georefSticker,
    handlePlanPinPlace,
    handlePinSelect,
    handleStickerTranslate,
    imageTransform,
    loading,
    mapFeatureCollection,
    pickingLot,
    pinPlacing,
    pinHoverId,
    pinsOpen,
    pinsPaintProject,
    planDisplay,
    previewProject,
    selectedId,
  ]);

  useEffect(() => {
    if (loading || error) {
      onParcelCountsChangeRef.current?.(null);
      return undefined;
    }
    onParcelCountsChangeRef.current?.(parcelCounts);
    return undefined;
  }, [error, loading, parcelCounts]);

  useEffect(() => () => {
    onParcelCountsChangeRef.current?.(null);
  }, []);

  useEffect(() => {
    if (!selectedId) {
      onParcelOpenChangeRef.current?.(null);
      return;
    }
    const feature = findCadFeature(project?.parcels, selectedId)
      || features.find((item) => getCadParcelId(item) === selectedId);
    if (feature) {
      onParcelOpenChangeRef.current?.(getCadParcelTooltipLabel(feature));
    }
  }, [selectedId, project, features]);

  useEffect(() => () => {
    onParcelOpenChangeRef.current?.(null);
  }, []);

  useEffect(() => {
    if (!deselectParcelRef) return undefined;
    deselectParcelRef.current = () => setSelectedId(null);
    return () => {
      deselectParcelRef.current = null;
    };
  }, [deselectParcelRef]);

  useEffect(() => {
    if (!sharePatch) return;
    if (sharePatch.slug && sharePatch.slug !== slug) return;
    setProject((prev) => (prev ? mergeCadProjectSummary(prev, sharePatch) : prev));
  }, [sharePatch, slug]);

  const handleSave = useCallback(async (parcelId, patch) => {
    setSaving(true);
    try {
      const result = await updateCadParcel({ id: parcelId, ...patch });
      if (!result.ok || !result.data) {
        toast.error(result.message || 'No se pudo guardar el lote.');
        return false;
      }
      setProject((prev) => {
        const next = withCadLotParcelCount(applyCadParcelPatch(prev, parcelId, result.data));
        onProjectLoadedRef.current?.(next);
        return next;
      });
      toast.success('Lote actualizado.');
      return true;
    } catch (err) {
      console.error('CAD en listas: fallo al guardar parcela', err);
      toast.error('No se pudo guardar el lote.');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const handlePhotosChange = useCallback((parcelId, patch) => {
    if (!parcelId || !patch) return;
    setProject((prev) => applyCadParcelPatch(prev, parcelId, {
      photos: Array.isArray(patch.photos) ? patch.photos : [],
      cover_image: patch.cover_image ?? null,
    }));
  }, []);

  const handleVideosChange = useCallback((parcelId, videos) => {
    if (!parcelId) return;
    setProject((prev) => applyCadParcelPatch(prev, parcelId, {
      videos: Array.isArray(videos) ? videos : [],
    }));
  }, []);

  const handleAttachmentsChange = useCallback((parcelId, attachments) => {
    if (!parcelId) return;
    setProject((prev) => applyCadParcelPatch(prev, parcelId, {
      attachments: Array.isArray(attachments) ? attachments : [],
    }));
  }, []);

  const handleDeleteParcel = useCallback(async (parcelId) => {
    const id = String(parcelId || '').trim();
    if (!id || deletingParcel || spaceMirror) return;
    const feature = findCadFeature(projectRef.current?.parcels, id)
      || (Array.isArray(projectRef.current?.parcels?.features)
        ? projectRef.current.parcels.features.find((item) => getCadParcelId(item) === id)
        : null);
    const label = feature ? getCadParcelLabel(feature) : 'este lote';
    const listName = projectRef.current?.name || projectRef.current?.slug || 'esta lista';
    const confirm = await confirmDialog({
      title: '¿Eliminar de la lista?',
      description: `Se eliminará definitivamente "${label}" de "${listName}".`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!confirm.isConfirmed) return;
    setDeletingParcel(true);
    try {
      const result = await deleteCadParcel({ id });
      if (!result.ok) {
        toast.error(result.message || 'No se pudo eliminar el lote.');
        return;
      }
      setProject((prev) => {
        const next = withCadLotParcelCount(removeCadParcelFromProject(prev, id));
        onProjectLoadedRef.current?.(next);
        return next;
      });
      setSelectedId(null);
      toast.success('Lote quitado de la lista.');
    } catch (err) {
      console.error('CAD en listas: fallo al eliminar parcela', err);
      toast.error('No se pudo eliminar el lote.');
    } finally {
      setDeletingParcel(false);
    }
  }, [deletingParcel, spaceMirror]);

  if (loading) {
    if (String(initialParcelId || '').trim()) {
      return <ShowroomParcelFormSkeleton />;
    }
    return <ShowroomParcelListSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ color: colors.textSecondary, fontSize: '13px' }}>{error}</Typography>
      </Box>
    );
  }

  if (canManage && georefOpen) {
    return (
      <CadGeorefPanel
        isEditing={georefEditing}
        waitingForLot={!georefSticker && pickingLot}
        refLotId={georefLotId}
        onLocateLot={onLocateLot}
        onClearMotherLot={clearMotherLot}
        onClearFromMap={canPublish ? handleClearFromMap : undefined}
        onUndoClearFromMap={canPublish ? handleUndoClearFromMap : undefined}
        clearFromMapPending={clearFromMapPending}
        sticker={georefSticker}
        canSnapToLot={canSnapCadGeorefStickerToLot({
          sticker: georefSticker,
          srcBounds: readCadStickerSrcBounds(project),
          lotBbox: georefLotBbox,
        })}
        hasLotSnapTarget={Array.isArray(georefLotBbox) && georefLotBbox.length >= 4}
        onSnapToLot={snapStickerToMotherLot}
        onScaleMulChange={(mul) => {
          setGeorefSticker((prev) => scaleCadGeorefSticker(prev, mul));
        }}
        onRotationDegChange={(deg) => {
          setGeorefSticker((prev) => rotateCadGeorefSticker(prev, (Number(deg) * Math.PI) / 180));
        }}
        saving={georefSaving}
        onCancel={handleGeorefCancel}
        onSubmit={handleGeorefSubmit}
        image={canvasImage}
        imageUploading={imageDraft.uploading}
        imageRemoved={imageDraft.removed}
        imageDraft={Boolean(imageDraft.draft)}
        imageError={imageDraft.draft?.status === 'error'}
        dirty={georefDirty}
        imageTransform={imageTransform}
        adjustingImage={adjustingImage}
        onImageUpload={canPublish ? handleImagePick : undefined}
        onImageDelete={canPublish ? handleImageRemove : undefined}
        onImageUndoDelete={canPublish ? handleImageUndoRemove : undefined}
        onImageTransformChange={canPublish ? commitImageTransform : undefined}
        onAdjustingImageChange={canPublish ? setAdjustingImage : undefined}
        onPickOnMap={canPublish && !georefSticker ? pickLotOnMap : undefined}
        showPlanImage={showPlanImage}
        onShowPlanImageChange={canPublish ? setShowPlanImageDraft : undefined}
      />
    );
  }

  if ((canManage || canEditCollection) && pinsOpen) {
    return (
      <CadProjectPinsPanel
        project={project}
        paintMode={planDisplay ? 'plan' : 'map'}
        placing={pinPlacing}
        onPlacingChange={setPinPlacing}
        placeCoords={pinPlaceCoords}
        onPlaceCoordsConsumed={() => setPinPlaceCoords(null)}
        onProjectPinsChange={handleProjectPinsChange}
        onDraftChange={setPinDraft}
        onHoverPinId={setPinHoverId}
        onCancel={closePins}
        selectedPinId={selectedPinId}
        onSelectPinId={setSelectedPinId}
        canDelete={!spaceMirror}
      />
    );
  }

  if (canManage || canEditCollection) {
    // Leads: dueño (`can_manage`) o invitado de Espacio. El editor solo de esta
    // lista no ve leads (la API le da 403).
    const canViewRecordLeads = canManage || spaceMirror;
    // R1 fase 3: "Consultas y leads" de la parcela abierta. V3-0: sin "Agregar
    // a lead" (intereses solo por consulta). El volver del lead reabre esta parcela.
    const selectedFeature = selectedId
      ? manageListFeatures.find((feature) => getCadParcelId(feature) === selectedId) || null
      : null;
    const parcelSubject = selectedFeature ? getCadParcelInquirySubject(selectedFeature) : '';
    const recordLeadsNode = canViewRecordLeads && selectedId ? (
      <RecordLeadsSection
        key={`record-leads-cad:${spaceOwnerUserId || 'own-space'}:${selectedId}`}
        itemKind="cad_parcel"
        recordId={selectedId}
        ownerUserId={spaceOwnerUserId}
        lastActivityAt={commercialById?.[String(selectedId)]?.last_activity_at || null}
        onOpenLead={onOpenLead ? (leadId) => onOpenLead(leadId, {
          parcelId: selectedId,
          label: parcelSubject ? `parcela ${parcelSubject}` : 'la parcela',
          project: {
            slug: project?.slug || slug,
            name: project?.name || listProject?.name || slug,
            id: getCadProjectId(project) || projectId || null,
            display_mode: project?.display_mode || null,
            can_manage: canManage,
            can_publish: canPublish,
          },
        }) : undefined}
      />
    ) : null;
    // Meta Ads E4: «Campañas» de la parcela, mismas condiciones que leads.
    const recordCampaignsNode = canViewRecordLeads && selectedId ? (
      <RecordCampaignsSection
        key={`record-campaigns-cad:${spaceOwnerUserId || 'own-space'}:${selectedId}`}
        itemKind="cad_parcel"
        recordId={selectedId}
        ownerUserId={spaceOwnerUserId}
        projectId={getCadProjectId(project) || projectId || null}
      />
    ) : null;
    // Comparación de mercado: «Precio vs. mercado» de la parcela (`custom_parcels.id`), mismas
    // condiciones que leads. Ver docs/maps/market-comparison.md.
    const renderMarketComparison = canViewRecordLeads && selectedId ? ({ onEdit, revision } = {}) => (
      <MarketComparisonCard
        key={`market-cad:${spaceOwnerUserId || 'own-space'}:${selectedId}`}
        itemKind="cad_parcel"
        recordId={selectedId}
        ownerUserId={spaceOwnerUserId}
        revision={revision}
        unitLabel={parcelSubject.replace(/^LOTE\b/, 'Lote')}
        onEdit={onEdit}
        center={geometryCenter(selectedFeature?.geometry)}
        onShowOnMap={onShowMarketComparables
          ? (view) => onShowMarketComparables({ ...view, source: 'lists', anchorId: String(selectedId) })
          : undefined}
      />
    ) : null;
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <ShowroomManagePanel
          features={manageListFeatures}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onSave={handleSave}
          onDelete={canEditCollection || spaceMirror ? undefined : handleDeleteParcel}
          onPhotosChange={canEditCollection ? undefined : handlePhotosChange}
          onVideosChange={canEditCollection ? undefined : handleVideosChange}
          onAttachmentsChange={canEditCollection ? undefined : handleAttachmentsChange}
          saving={saving}
          deleting={canEditCollection ? false : deletingParcel}
          listingCoreOnly={canEditCollection}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          parcelSearch={parcelSearch}
          onParcelSearchChange={setParcelSearch}
          inquiryByItem={inquiryByItem}
          recordLeads={recordLeadsNode}
          recordCampaigns={recordCampaignsNode}
          renderMarketComparison={renderMarketComparison}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CadVisitorParcelList
        features={scopedFeatures}
        selectedId={selectedId}
        onSelect={setSelectedId}
        parcelSearch={parcelSearch}
        onParcelSearchChange={setParcelSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        branding={visitorBranding}
      />
    </Box>
  );
}
